import { axiosInstance } from './api';
import type { FrameBox } from '@/components/ui/frame-with-polygon';

// #159 — «Калибровка полигонов»: one row per polygon of the branch, threshold
// input + a 5-examples-per-state matrix right there. See services/services/
// api/analytics.py calibration_zone_polygons / calibration_polygon_examples.

export type CalibrationPolygonKind =
  | 'rack' | 'shelf' | 'table' | 'queue' | 'cashier' | 'staff' | 'order_waiting';

/** #161 — a threshold that's obviously bad by the SAME criteria as
 *  linzahelper's morning digest (monitor/analysis_digest.py +
 *  monitor/digest_sql/*.sql): never-fires/over-flagged (shelf),
 *  threshold-unreachable (queue), never-dirty/always-dirty/many-false-alerts
 *  (table). `badness` is only meaningful WITHIN one verdict, not across
 *  verdicts/kinds — it's a relative loudness for sorting, not a universal
 *  scale (a shelf's percentage-point gap isn't comparable to a table's
 *  judgment count). */
export interface CalibrationDiagnosis {
  verdict: string;
  note: string;
  badness: number;
  /** Сила (#3003): high — красная, mid — оранжевая, low — бледная. */
  severity?: CalibrationSeverity;
}

/** info — факт без алерта (#3343): серым текстом, полигон не считается проблемным. */
export type CalibrationSeverity = 'high' | 'mid' | 'low' | 'info';

export interface CalibrationPolygon {
  kind: CalibrationPolygonKind;
  id: string;
  name: string;
  camera_id: string;
  rack_id?: string;
  rack_name?: string;
  thresholds: Record<string, number | boolean>;
  /** id to PATCH thresholds against — differs from `id` for queue, where
   *  `id` addresses the CheckoutZone (for Зонирование deep-links) but the
   *  threshold field lives on the CheckoutQueue row. Null for kinds with no
   *  editable threshold (table, cashier, staff, order_waiting — staff lost
   *  its max_workers in #127). */
  threshold_entity_id: string | null;
  /** Очередь/касса: PolygonArea группы — редактор полигона очереди в калибровке (Алексей 21.10). */
  area_ids?: string[];
  diagnosis?: CalibrationDiagnosis | null;
  /** Эффективная нейросеть полки/стеллажа (AI197): yolo — количественная, pct — процентная. */
  model?: 'pct' | 'yolo';
  /** Полка: собственный флаг (null = как у стеллажа). */
  shelf_predict_percentage?: boolean | null;
  /** Полка: как размечена полезная площадь — обведено полезное, обведено лишнее, не размечена. */
  useful_area_mode?: 'include' | 'exclude' | null;
  /** Стеллаж: хоть у одной полки свой флаг — «Настроено по полкам». */
  shelves_configured?: boolean;
  /** Полка: отметки «до исправления» (AI197) — полигон/модель изменены без подбора делителя
   *  или порога, дрейф распределения неделя к неделе или после выкладки модели. */
  flags?: CalibrationFlag[];
  /** Последняя правка калибровки (журнал): полка — своя, полигона, модели стеллажа; стеллаж — с полками. */
  last_edit?: CalibrationEdit;
  /** Сутки после правки: отметки считаются по данным после неё, в сортировке «плохие наверх» — внизу. */
  under_review?: boolean;
  /** Алерты валидаторам за 7 дн. (витрина, стол, очередь): всего и ложных — «полно»/не подтвердил. */
  alerts_7d?: { n: number; false: number } | null;
}

export interface CalibrationEdit {
  who: string;
  at: string;
  field: string;
  /** порог, поправка модели, полезная площадь, нейросеть, полигон… */
  label: string;
  /** null у полигона — точки не показываем. */
  old: string | null;
  new: string | null;
}

export interface CalibrationFlag {
  /** polygon_no_divisor, model_no_*, drift_*, atypical_*, false_alerts_not_fixable_by_threshold,
   *  threshold_can_be_stricter, report_corrections (валидаторы правят категорию в отчётах, #3003)… */
  code: string;
  note: string;
  since?: string;
  severity?: CalibrationSeverity;
}

export const getCalibrationPolygons = (zoneId: string) =>
  axiosInstance.get<{ polygons: CalibrationPolygon[] }>(
    '/core/api/analytics/calibration-polygons/',
    { params: { zone_id: zoneId } },
  );

export interface CalibrationExample {
  image_url: string;
  at: string;
  frame_id: number;
  boxes: FrameBox[];
  /** Полигоны самого трека на этом кадре (очередь/касса) — #1170. */
  polygons?: number[][][];
}

export interface CalibrationExampleRow {
  /** Raw state — people count for queue/cashier/staff/order_waiting, the
   *  0/10/…/100 ABSOLUTE fill tier for rack/shelf, the gate keyword
   *  (occupied/dirty/shifted/free) for table. Use `label` for display. */
  state: number | string;
  /** Human-readable row label — always present, track-appropriate. */
  label: string;
  /** Shelf only: the tier re-expressed against this shelf's own
   *  max_effective_percentage (#160's "three parameters" note — photos are
   *  picked by ABSOLUTE fill, but the label people read is relative to what
   *  the shelf can physically hold). */
  relative_label?: number;
  percent: number;
  count: number;
  examples: CalibrationExample[];
}

export interface CalibrationExamplesResponse {
  kind: string;
  /** True for a kind with no matrix implementation at all — shouldn't
   *  happen for any real CalibrationPolygonKind after #160, kept as a
   *  defensive fallback the FE still renders sensibly. */
  not_implemented?: boolean;
  from?: string;
  to?: string;
  rows: CalibrationExampleRow[];
}

export const getCalibrationExamples = (
  kind: string,
  polygonId: string,
  zoneId: string,
  from: string,
  to: string,
) =>
  axiosInstance.get<CalibrationExamplesResponse>(
    '/core/api/analytics/calibration-examples/',
    { params: { kind, polygon_id: polygonId, zone_id: zoneId, from, to } },
  );
